import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ApiClientError } from '../../../services/apiClient';
import { listarDashboardDocumentos } from '../../../services/alertasApi';
import { listarEmpresas } from '../../../services/empresasApi';
import {
  DocumentoDashboardItem,
  DocumentosDashboardFilters,
  DocumentosDashboardResponse,
  DocumentoDashboardStatus,
} from '../../../types/documentosDashboard';
import { EmpresaOperacional, EmpresaPapel } from '../../../types/empresa';
import SummaryCards from './SummaryCards';
import ExpirationChart from './ExpirationChart';
import LicenseTable from './LicenseTable';

const PAGE_SIZE = 10;
const EMPTY_RESPONSE: DocumentosDashboardResponse = {
  items: [],
  summary: { total: 0, regular: 0, vencendo: 0, vencido: 0, pendente_validacao: 0, invalido: 0 },
  vencimentos: { ano: new Date().getFullYear(), porMes: Array<number>(12).fill(0) },
  page: 1,
  pageSize: PAGE_SIZE,
  total: 0,
};
const papeis: Array<{ value: EmpresaPapel; label: string }> = [
  { value: 'gerador', label: 'Gerador' },
  { value: 'coletor', label: 'Coletor' },
  { value: 'transportador', label: 'Transportador' },
  { value: 'armazenador_temporario', label: 'Armazenador temporário' },
  { value: 'tratador', label: 'Tratador' },
  { value: 'destinador', label: 'Destinador' },
];
const statuses: Array<{ value: DocumentoDashboardStatus; label: string }> = [
  { value: 'regular', label: 'Regular' },
  { value: 'vencendo', label: 'Próximo do vencimento' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'pendente_validacao', label: 'Pendente de validação' },
  { value: 'invalido', label: 'Inválido' },
];
const ufs = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

type DraftFilters = {
  empresaId: string;
  papel: '' | EmpresaPapel;
  tipoDocumento: string;
  uf: string;
  status: '' | DocumentoDashboardStatus;
  diasVencimento: number;
};
const INITIAL_FILTERS: DraftFilters = { empresaId: '', papel: '', tipoDocumento: '', uf: '', status: '', diasVencimento: 30 };

function formatError(error: unknown): string {
  if (error instanceof ApiClientError) return `${error.code}: ${error.message}`;
  return error instanceof Error ? error.message : 'Não foi possível carregar o dashboard documental.';
}

const LicenseDashboardPage: React.FC = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DocumentosDashboardResponse>(EMPTY_RESPONSE);
  const [empresas, setEmpresas] = useState<EmpresaOperacional[]>([]);
  const [draft, setDraft] = useState<DraftFilters>(INITIAL_FILTERS);
  const [filters, setFilters] = useState<Omit<DocumentosDashboardFilters, 'page' | 'pageSize'>>({ diasVencimento: 30 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(data.total / PAGE_SIZE)), [data.total]);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      setData(await listarDashboardDocumentos(accessToken, { ...filters, page, pageSize: PAGE_SIZE }));
    } catch (loadError: unknown) {
      setData(EMPTY_RESPONSE);
      setError(formatError(loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken, filters, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!accessToken) return;
    listarEmpresas(accessToken, { page: 1, pageSize: 100 })
      .then((response) => setEmpresas(response.items))
      .catch(() => setEmpresas([]));
  }, [accessToken]);

  const applyFilters = () => {
    setPage(1);
    setFilters({
      empresaId: draft.empresaId || undefined,
      papel: draft.papel || undefined,
      tipoDocumento: draft.tipoDocumento.trim() || undefined,
      uf: draft.uf || undefined,
      status: draft.status || undefined,
      diasVencimento: draft.diasVencimento,
    });
  };
  const clearFilters = () => {
    setDraft(INITIAL_FILTERS);
    setPage(1);
    setFilters({ diasVencimento: 30 });
  };
  const openDocument = (documento: DocumentoDashboardItem) => {
    navigate(`/backoffice/validacao-licencas?documentoId=${documento.documentoId}`);
  };

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Dashboard de documentos e licenças</h2>
          <p className="mt-1 text-sm text-gray-600">Visão persistente dos documentos oficiais. Upload não representa validação.</p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60">
          <RefreshCw size={16}/>Atualizar
        </button>
      </header>

      <SummaryCards total={data.summary.total} regulares={data.summary.regular} pendentes={data.summary.pendente_validacao} vencendo={data.summary.vencendo} vencidas={data.summary.vencido} invalidas={data.summary.invalido}/>

      <section className="grid gap-3 rounded-md border border-gray-200 bg-white p-4 md:grid-cols-3 xl:grid-cols-6">
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Empresa<select aria-label="Empresa" value={draft.empresaId} onChange={(event) => setDraft((current) => ({ ...current, empresaId: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case"><option value="">Todas</option>{empresas.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.razaoSocial}</option>)}</select></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Papel<select aria-label="Papel" value={draft.papel} onChange={(event) => setDraft((current) => ({ ...current, papel: event.target.value as DraftFilters['papel'] }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case"><option value="">Todos</option>{papeis.map((papel) => <option key={papel.value} value={papel.value}>{papel.label}</option>)}</select></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tipo documental<input aria-label="Tipo documental" value={draft.tipoDocumento} onChange={(event) => setDraft((current) => ({ ...current, tipoDocumento: event.target.value }))} placeholder="Licença, alvará..." className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case"/></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status<select aria-label="Status documental" value={draft.status} onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value as DraftFilters['status'] }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case"><option value="">Todos</option>{statuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}</select></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">UF<select aria-label="UF" value={draft.uf} onChange={(event) => setDraft((current) => ({ ...current, uf: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case"><option value="">Todas</option>{ufs.map((uf) => <option key={uf}>{uf}</option>)}</select></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Janela (dias)<input aria-label="Janela de vencimento" type="number" min={1} max={365} value={draft.diasVencimento} onChange={(event) => setDraft((current) => ({ ...current, diasVencimento: Number(event.target.value) }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case"/></label>
        <div className="flex gap-2 md:col-span-3 xl:col-span-6">
          <button type="button" onClick={applyFilters} className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800"><Search size={16}/>Aplicar filtros</button>
          <button type="button" onClick={clearFilters} className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700"><X size={16}/>Limpar</button>
        </div>
      </section>

      {error ? <div role="alert" className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900"><AlertTriangle size={18}/><span>{error}</span></div> : null}
      <ExpirationChart ano={data.vencimentos.ano} porMes={data.vencimentos.porMes}/>
      <section className="rounded-md border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">{loading ? 'Carregando documentos...' : `${data.total} documento(s) encontrado(s)`}</div>
        <LicenseTable documentos={data.items} onOpen={openDocument}/>
      </section>
      <footer className="flex items-center justify-between text-sm text-gray-600">
        <span>Página {page} de {totalPages}</span>
        <div className="flex gap-2"><button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)} className="rounded-md border px-3 py-2 disabled:opacity-40">Anterior</button><button type="button" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)} className="rounded-md border px-3 py-2 disabled:opacity-40">Próxima</button></div>
      </footer>
    </div>
  );
};

export default LicenseDashboardPage;
